/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { Formik, Field as FormikField } from 'formik'
import ValidationSchema from './EditFormValidation'
import { COMPLETE_STATUSES } from './EditFormConstants'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import Modal from '../../../../components/Modal'
import Field from '../../../../components/shared/Field'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'
import { InfiniteScrollSelect } from '../../../../components/InfiniteScrollSelect'
import { getOptionsForAsyncPicker } from '../../../../services/api/metadata'
import { getWatermarksForPicker } from '../../../../services/api/watermarks'
import { changeRequestTypesApiService } from '../../../../services/api/api.changerequesttypes'
import { Select } from '../../../../components/Select'

const HalfWidthDiv = styled.div`
  width: 50%;
  padding-right: 23px;
  float: left;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

const DivLeftIntended = styled.div`
  padding-left: 10px;
`

const InfoIconWrapper = styled.span`
  margin-left: 5px;
  color: #337ab7;
  cursor: pointer;
  position: relative;

  .info-box-tooltip {
    display: none;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    color: #666666;
    padding: 12px 16px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid #ddd;
    white-space: normal;
    width: 300px;
    margin-bottom: 10px;
    z-index: 1000;
    font-size: 12px;
    line-height: 1.2;
    font-weight: normal;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  &:hover .info-box-tooltip {
    display: block;
  }

  .info-box-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-top-color: white;
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1));
  }
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  initialValues: any
  loading?: Promise<any> | null
  isLoadingData?: boolean
  templatesFiles?: any[]
  availableSignatureForms?: any[]
}

const EditForm: React.FC<EditFormProps> = ({
  onSubmit,
  doClose,
  isEdit,
  initialValues,
  loading,
  isLoadingData = false,
  templatesFiles = [],
  availableSignatureForms = []
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const { isEnableDocDetailsExpirationDate = false } = context.service.init.getFeaturesInfo?.() || {}

  const closeDialog = () => {
    if (doClose) {
      doClose()
    }
  }

  const handleFormSubmit = async (values: any, actions: any) => {
    setHasSubmitted(true)
    return onSubmit(values, actions)
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        validateOnBlur={hasSubmitted}
      >
        {({ values, errors, touched, handleSubmit, handleChange, handleBlur, setFieldValue }) => {
          // Track changes to properties and reset dependent date fields if needed
          useEffect(() => {
            const allProperties = [
              ...(values?.optionalMetadataTemplates || []),
              ...(values?.requiredMetadataTemplates || [])
            ]

            // Check if type information is available
            const hasTypeInfo = allProperties.some((opt: any) => opt?.type || opt?.propertyType)

            // Only filter and validate if we have type information
            if (hasTypeInfo) {
              const allDateOptions = allProperties
                .filter((option: any) => option?.type === 'DATETIME' || option?.propertyType === 'DATETIME')
                .map((option: any) => option.value || option.id)

              // Reset customLastDateMetadataTemplate if it's no longer in the available DATETIME options
              if (
                values.customLastDateMetadataTemplate &&
                !allDateOptions.includes(values.customLastDateMetadataTemplate)
              ) {
                setFieldValue('customLastDateMetadataTemplate', null)
              }

              // Reset customNextDateMetadataTemplate if it's no longer in the available DATETIME options
              if (
                values.customNextDateMetadataTemplate &&
                !allDateOptions.includes(values.customNextDateMetadataTemplate)
              ) {
                setFieldValue('customNextDateMetadataTemplate', null)
              }
            } else {
              // If no type info, validate against all property IDs
              const allPropertyIds = allProperties.map((option: any) => option.value || option.id)

              if (
                values.customLastDateMetadataTemplate &&
                !allPropertyIds.includes(values.customLastDateMetadataTemplate)
              ) {
                setFieldValue('customLastDateMetadataTemplate', null)
              }

              if (
                values.customNextDateMetadataTemplate &&
                !allPropertyIds.includes(values.customNextDateMetadataTemplate)
              ) {
                setFieldValue('customNextDateMetadataTemplate', null)
              }
            }
          }, [values.optionalMetadataTemplates, values.requiredMetadataTemplates])

          return (
            <div>
              <Loader blocking={!!loading || isLoadingData}>
                <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <form>
                    <HalfWidthDiv>
                      <StyledFormGroup className="w100p">
                        <Form.Label>{translate('NAME')}</Form.Label>
                        <Field
                          name="name"
                          type="text"
                          maxLength={255}
                          autoFocus
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessageStyled name="name" />
                      </StyledFormGroup>

                      <StyledFormGroup className="w100p">
                        <Form.Label>{translate('DESCRIPTION')}</Form.Label>
                        <Field
                          type="text"
                          name="description"
                          className="form-control field-control"
                          id="f1db725e-91c7-4629-8568-f09436af9f31"
                        />
                        <ErrorMessageStyled name="description" />
                      </StyledFormGroup>

                      {/* Properties Section */}
                      <Form.Label>{translate('PROPERTIES')}</Form.Label>
                      <DivLeftIntended>
                        <StyledFormGroup>
                          <Form.Label>{translate('OPTIONAL_PROPERTIES')}</Form.Label>
                          <InfiniteScrollSelect
                            placeholder={translate('SELECT_PROPERTIES...')}
                            name="optionalMetadataTemplates"
                            value={values?.optionalMetadataTemplates || []}
                            asyncLoadFn={async (search: string, page: number) => {
                              const result = await getOptionsForAsyncPicker(search, page)
                              // Ensure type field is included in options
                              return result
                            }}
                            onChange={(option: any) => {
                              console.log('optional properties selected: ', option)
                              // Ensure we preserve the type field
                              setFieldValue('optionalMetadataTemplates', option || [])
                            }}
                            multi
                            searchable
                            id="optional-properties-select"
                          />
                          <ErrorMessageStyled name="optionalMetadataTemplates" />
                        </StyledFormGroup>

                        <StyledFormGroup>
                          <Form.Label>{translate('REQUIRED_PROPERTIES')}</Form.Label>
                          <InfiniteScrollSelect
                            placeholder={translate('SELECT_PROPERTIES...')}
                            name="requiredMetadataTemplates"
                            value={values?.requiredMetadataTemplates || []}
                            asyncLoadFn={async (search: string, page: number) => {
                              const result = await getOptionsForAsyncPicker(search, page)
                              // Ensure type field is included in options
                              return result
                            }}
                            onChange={(option: any) => {
                              console.log('required properties selected: ', option)
                              // Ensure we preserve the type field
                              setFieldValue('requiredMetadataTemplates', option || [])
                            }}
                            multi
                            searchable
                            id="required-properties-select"
                          />
                          <ErrorMessageStyled name="requiredMetadataTemplates" />
                        </StyledFormGroup>
                      </DivLeftIntended>
                      {isEnableDocDetailsExpirationDate ? (
                        <StyledFormGroup controlId="2f5d121e-1dec-471d-8546-d3bcf2688c61">
                          <div className="checkbox">
                            <label htmlFor="2f5d121e-1dec-471d-8546-d3bcf2688c61">
                              <FormikField
                                type="checkbox"
                                name="requireExpirationDate"
                                checked={!!values.requireExpirationDate}
                                id="2f5d121e-1dec-471d-8546-d3bcf2688c61"
                              />
                              <b>{translate('DOC_TYPE_PROP_REQUIRE_EXPIRATION_DATE')}</b>
                            </label>
                          </div>
                        </StyledFormGroup>
                      ) : null}

                      {/* Watermarks Dropdown */}
                      <StyledFormGroup>
                        <Form.Label>{translate('WATERMARKS_TITLE')}</Form.Label>
                        <InfiniteScrollSelect
                          menuPortalTarget={document.body}
                          placeholder={translate('SELECT_WATERMARKS...')}
                          name="watermarkIds"
                          value={values?.watermarkIds || []}
                          asyncLoadFn={async (search: string, page: number) => {
                            return await getWatermarksForPicker(search, page)
                          }}
                          onChange={(option: any) => {
                            setFieldValue('watermarkIds', option || [])
                          }}
                          multi
                          searchable
                          id="watermarks-select"
                        />
                        <ErrorMessageStyled name="watermarkIds" />
                      </StyledFormGroup>

                      {/* Document Template Dropdown */}
                      <StyledFormGroup controlId="afb6e904-6e92-4f54-a46f-8f6932022276">
                        <Form.Label>{translate('SETTINGS_EDIT_DOCUMENT_TEMPLATE')}</Form.Label>
                        <Select
                          placeholder={translate('SELECT_FILE_TEMPLATE')}
                          name="templateFileId"
                          value={values?.templateFileId?.value}
                          options={templatesFiles}
                          onChange={(option: any) => {
                            setFieldValue('templateFileId', option || null)
                          }}
                          searchable
                          id="afb6e904-6e92-4f54-a46f-8f6932022276"
                        />
                        <ErrorMessageStyled name="templateFileId" />
                      </StyledFormGroup>

                      {/* Signature Form Template Dropdown */}
                      <StyledFormGroup controlId="0a894346-c5aa-40e7-8add-357e03093756">
                        <Form.Label>{translate('SIGNATURE_FORM_TEMPLATE')}</Form.Label>
                        <Select
                          placeholder={translate('SELECT_SIGNATURE_FORM')}
                          name="signatureTemplateFileId"
                          value={values?.signatureTemplateFileId?.value}
                          options={availableSignatureForms}
                          onChange={(option: any) => {
                            setFieldValue('signatureTemplateFileId', option || null)
                          }}
                          searchable
                          id="0a894346-c5aa-40e7-8add-357e03093756"
                        />
                        <ErrorMessageStyled name="signatureTemplateFileId" />
                      </StyledFormGroup>

                      {values.signatureTemplateFileId && (
                        <DivLeftIntended>
                          <StyledFormGroup className="mb0">
                            <Form.Label>{translate('ADD_TO')}</Form.Label>
                          </StyledFormGroup>
                          <div className="radio">
                            <label htmlFor="7d40391a-cea2-497c-a5a1-7b7ca0a82842">
                              <FormikField
                                type="radio"
                                name="addSignaturePageToFront"
                                value="true"
                                checked={values.addSignaturePageToFront === true}
                                onChange={() => setFieldValue('addSignaturePageToFront', true)}
                                id="7d40391a-cea2-497c-a5a1-7b7ca0a82842"
                                inline
                              />
                              <b>{translate('FIRST_PAGE')}</b>
                            </label>

                            <div className="radio">
                              <label htmlFor="signature-page-last">
                                <FormikField
                                  type="radio"
                                  name="addSignaturePageToFront"
                                  value="false"
                                  checked={values.addSignaturePageToFront === false}
                                  onChange={() => setFieldValue('addSignaturePageToFront', false)}
                                  id="signature-page-last"
                                  inline
                                />
                                <b>{translate('LAST_PAGE')}</b>
                              </label>
                            </div>
                          </div>
                        </DivLeftIntended>
                      )}

                      <StyledFormGroup controlId="workflow-template-group">
                        <Form.Label>{translate('WF_TEMPLATE_LABEL')}</Form.Label>
                        <InfiniteScrollSelect
                          menuPortalTarget={document.body}
                          placeholder={translate('SELECT_WF_TEMPLATE_PLACEHOLDER')}
                          name="workflowTemplate"
                          value={values?.workflowTemplate || null}
                          asyncLoadFn={async (search: string, page: number) => {
                            const response: any = await changeRequestTypesApiService.getOptionsForAsyncPicker(
                              search,
                              page
                            )

                            // Transform the response to match AsyncLoadResponse format
                            const elements = response?.elements || []
                            const totalElements = response?.totalElements || 0

                            // Calculate if there are more pages
                            const hasMore = elements.length > 0 && (page + 1) * 25 < totalElements

                            // Map to options format with value and label
                            const options = elements.map((item: any) => ({
                              value: item.id,
                              label: item.name,
                              id: item.id,
                              name: item.name
                            }))

                            return { options, hasMore }
                          }}
                          onChange={(option: any) => {
                            setFieldValue('workflowTemplate', option)
                          }}
                          searchable
                          id="workflow-template-select"
                        />
                        <ErrorMessageStyled name="workflowTemplate" />
                      </StyledFormGroup>
                    </HalfWidthDiv>

                    {/* Right Column - Checkboxes */}
                    <HalfWidthDiv className="pull-left">
                      <StyledFormGroup controlId="415a8016-6bf9-4b21-bb41-dbd0f63a0af9" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="415a8016-6bf9-4b21-bb41-dbd0f63a0af9">
                            <FormikField
                              type="checkbox"
                              name="allowOverridesDuringCR"
                              checked={!!values.allowOverridesDuringCR}
                              id="415a8016-6bf9-4b21-bb41-dbd0f63a0af9"
                            />
                            <b>{translate('SETTINGS_EDIT_ALLOW_EDITING_OF_ACTIVE_CR')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      <StyledFormGroup controlId="973a0eb5-156d-4c83-abbc-0dd55583c52a" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="973a0eb5-156d-4c83-abbc-0dd55583c52a">
                            <FormikField
                              type="checkbox"
                              name="requireJustification"
                              checked={!!values.requireJustification}
                              id="973a0eb5-156d-4c83-abbc-0dd55583c52a"
                            />
                            <b>{translate('SETTINGS_REQUIRE_JUSTI')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      <StyledFormGroup controlId="a7aa3c35-c071-4fa0-9e52-6d1cc35da31e" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="a7aa3c35-c071-4fa0-9e52-6d1cc35da31e">
                            <FormikField
                              type="checkbox"
                              name="requireRejectReason"
                              checked={!!values.requireRejectReason}
                              id="a7aa3c35-c071-4fa0-9e52-6d1cc35da31e"
                            />
                            <b>{translate('SETTINGS_REQUIRE_REJECTION_REASON2')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      <Form.Label>{translate('ALLOW_EDITING_OF_PROPERTY_VALUES_WITHIN')}</Form.Label>
                      <DivLeftIntended>
                        <StyledFormGroup controlId="5c28528d-ee7f-49d4-92a5-d86197ca9c5f" className="w100p">
                          <div className="checkbox">
                            <label htmlFor="5c28528d-ee7f-49d4-92a5-d86197ca9c5f">
                              <FormikField
                                type="checkbox"
                                name="allowPropertiesChangeDuringEdit"
                                checked={!!values.allowPropertiesChangeDuringEdit}
                                id="5c28528d-ee7f-49d4-92a5-d86197ca9c5f"
                              />
                              <b>{translate('EDIT_PHASE')}</b>
                            </label>
                          </div>
                        </StyledFormGroup>

                        <StyledFormGroup controlId="5599a1cb-c9a8-43e6-87be-60e2551cc6ba" className="w100p">
                          <div className="checkbox">
                            <label htmlFor="5599a1cb-c9a8-43e6-87be-60e2551cc6ba">
                              <FormikField
                                type="checkbox"
                                name="allowPropertiesChangeDuringReview"
                                checked={!!values.allowPropertiesChangeDuringReview}
                                id="5599a1cb-c9a8-43e6-87be-60e2551cc6ba"
                              />
                              <b>{translate('REVIEW_PHASE')}</b>
                            </label>
                          </div>
                        </StyledFormGroup>
                      </DivLeftIntended>

                      {/* Documents in Completed State Dropdown */}
                      <StyledFormGroup controlId="c0536e7e-5a79-4aa0-883d-c5379e9fbd27">
                        <Form.Label>{translate('SETTINGS_EDIT_DOCUMENT_TYPE_LABEL_COMPLETED')}</Form.Label>
                        <Select
                          name="_customEffectiveStateLabelType"
                          value={values?._customEffectiveStateLabelType}
                          options={Object.entries(COMPLETE_STATUSES).map(([key, value]) => ({
                            value: value.toLowerCase(),
                            label: translate(key === 'CUSTOM' ? 'CUSTOM...' : `FSI_COMPLETE_STATUS_TYPE_${key}`)
                          }))}
                          onChange={(option: any) => {
                            setFieldValue('_customEffectiveStateLabelType', option ? option.value : null)
                          }}
                          searchable
                          id="c0536e7e-5a79-4aa0-883d-c5379e9fbd27"
                        />
                        <ErrorMessageStyled name="_customEffectiveStateLabelType" />
                      </StyledFormGroup>

                      {/* Custom Label Input - shown when CUSTOM is selected */}
                      {values._customEffectiveStateLabelType === 'custom' && (
                        <StyledFormGroup controlId="95778c78-71ae-43f8-b07e-198068d77014" className="w100p">
                          <Field
                            type="text"
                            name="_customEffectiveStateLabelText"
                            maxLength={255}
                            value={values._customEffectiveStateLabelText || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            id="95778c78-71ae-43f8-b07e-198068d77014"
                          />
                          <ErrorMessageStyled name="_customEffectiveStateLabelText" />
                        </StyledFormGroup>
                      )}

                      {/* Allow Read & Acknowledge Checkbox */}
                      <StyledFormGroup controlId="92a49494-a6f8-46a8-a09e-5fb9f1be6511" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="92a49494-a6f8-46a8-a09e-5fb9f1be6511">
                            <FormikField
                              type="checkbox"
                              name="effectiveTimeDaysEnabled"
                              checked={!!values.effectiveTimeDaysEnabled}
                              id="92a49494-a6f8-46a8-a09e-5fb9f1be6511"
                            />
                            <b>{translate('ALLOW_R_A')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Duration Field - shown when Allow R&A is checked */}
                      {values.effectiveTimeDaysEnabled && (
                        <DivLeftIntended>
                          <StyledFormGroup controlId="52804047-a35d-485c-8bf4-0b300818ce94">
                            <Form.Label>{translate('SETTINGS_EDIT_TYPE_DURATION')}</Form.Label>
                            <Field
                              type="text"
                              name="effectiveTimeDays"
                              maxLength={255}
                              value={values.effectiveTimeDays || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="52804047-a35d-485c-8bf4-0b300818ce94"
                            />
                            <ErrorMessageStyled name="effectiveTimeDays" />
                          </StyledFormGroup>
                        </DivLeftIntended>
                      )}

                      {/* Periodic Review Checkbox */}
                      <StyledFormGroup controlId="8c940d29-8d4b-47ed-9fa3-743687e6654e" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="8c940d29-8d4b-47ed-9fa3-743687e6654e">
                            <FormikField
                              type="checkbox"
                              name="expirationTimeMonthsEnabled"
                              checked={!!values.expirationTimeMonthsEnabled}
                              id="8c940d29-8d4b-47ed-9fa3-743687e6654e"
                            />
                            <b>{translate('PERIODIC_REVIEW')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Periodic Review Options - shown when Periodic Review is checked */}
                      {values.expirationTimeMonthsEnabled && (
                        <DivLeftIntended>
                          {/* Radio Button 1: Relative to Document Effective Date */}
                          <StyledFormGroup controlId="periodic-review-radio-1" className="w100p">
                            <div className="radio">
                              <label htmlFor="periodic-review-effective-date">
                                <FormikField
                                  type="radio"
                                  name="isPeriodicReviewCustomDateEnabled"
                                  value="false"
                                  checked={!values.isPeriodicReviewCustomDateEnabled}
                                  onChange={() => setFieldValue('isPeriodicReviewCustomDateEnabled', false)}
                                  id="periodic-review-effective-date"
                                  inline
                                />
                                <b>{translate('RELATIVE_DOC_EFFECTIVE_DATE')}</b>
                              </label>
                            </div>
                          </StyledFormGroup>

                          {/* Radio Button 2: Relative to Custom Date Property */}
                          <StyledFormGroup controlId="periodic-review-radio-2" className="w100p">
                            <div className="radio">
                              <label htmlFor="periodic-review-custom-date">
                                <FormikField
                                  type="radio"
                                  name="isPeriodicReviewCustomDateEnabled"
                                  value="true"
                                  checked={values.isPeriodicReviewCustomDateEnabled}
                                  onChange={() => setFieldValue('isPeriodicReviewCustomDateEnabled', true)}
                                  id="periodic-review-custom-date"
                                  inline
                                />
                                <b>{translate('RELATIVE_CUSTOM_DATE_PROP')}</b>
                              </label>
                            </div>
                          </StyledFormGroup>

                          {/* Custom Date Property Fields - shown when Custom Date radio is selected */}
                          {values.isPeriodicReviewCustomDateEnabled && (
                            <DivLeftIntended>
                              {/* Last Periodic Review Date Dropdown */}
                              <StyledFormGroup controlId="b6d0a2f7-f91a-4c92-84db-a6e470e00626">
                                <Form.Label>{translate('LAST_PERIODIC_REVIEW_DATE')}</Form.Label>
                                <Select
                                  placeholder={translate('SELECT_PROP')}
                                  name="customLastDateMetadataTemplate"
                                  value={values?.customLastDateMetadataTemplate}
                                  options={(() => {
                                    const allProperties = [
                                      ...(values?.optionalMetadataTemplates || []),
                                      ...(values?.requiredMetadataTemplates || [])
                                    ]

                                    // Check if any property has type information
                                    const hasTypeInfo = allProperties.some((opt: any) => opt?.type || opt?.propertyType)

                                    // Filter for DATETIME only if type information is available
                                    return hasTypeInfo
                                      ? allProperties
                                          .filter(
                                            (option: any) =>
                                              option?.type === 'DATETIME' || option?.propertyType === 'DATETIME'
                                          )
                                          .map((option: any) => ({
                                            value: option.value || option.id,
                                            label: option.label || option.name,
                                            type: option.type || option.propertyType
                                          }))
                                      : allProperties.map((option: any) => ({
                                          value: option.value || option.id,
                                          label: option.label || option.name
                                        }))
                                  })()}
                                  onChange={(option: any) => {
                                    setFieldValue('customLastDateMetadataTemplate', option ? option.value : null)
                                  }}
                                  searchable
                                  id="b6d0a2f7-f91a-4c92-84db-a6e470e00626"
                                />
                                <ErrorMessageStyled name="customLastDateMetadataTemplate" />
                              </StyledFormGroup>

                              {/* Next Periodic Review Date Dropdown */}
                              <StyledFormGroup controlId="699abc51-5012-416c-94c2-567330fd9cdb">
                                <Form.Label>{translate('NEXT_PERIODIC_REVIEW_DATE')}</Form.Label>
                                <Select
                                  placeholder={translate('SELECT_PROP')}
                                  name="customNextDateMetadataTemplate"
                                  value={values?.customNextDateMetadataTemplate}
                                  options={(() => {
                                    const allProperties = [
                                      ...(values?.optionalMetadataTemplates || []),
                                      ...(values?.requiredMetadataTemplates || [])
                                    ]

                                    // Check if any property has type information
                                    const hasTypeInfo = allProperties.some((opt: any) => opt?.type || opt?.propertyType)

                                    // Filter for DATETIME only if type information is available
                                    return hasTypeInfo
                                      ? allProperties
                                          .filter(
                                            (option: any) =>
                                              option?.type === 'DATETIME' || option?.propertyType === 'DATETIME'
                                          )
                                          .map((option: any) => ({
                                            value: option.value || option.id,
                                            label: option.label || option.name,
                                            type: option.type || option.propertyType
                                          }))
                                      : allProperties.map((option: any) => ({
                                          value: option.value || option.id,
                                          label: option.label || option.name
                                        }))
                                  })()}
                                  onChange={(option: any) => {
                                    setFieldValue('customNextDateMetadataTemplate', option ? option.value : null)
                                  }}
                                  searchable
                                  id="699abc51-5012-416c-94c2-567330fd9cdb"
                                />
                                <ErrorMessageStyled name="customNextDateMetadataTemplate" />
                              </StyledFormGroup>
                            </DivLeftIntended>
                          )}

                          {/* Frequency Field - always shown when Periodic Review is checked */}
                          <StyledFormGroup controlId="2d763afc-a310-494c-925c-4561e281e93a-frequency">
                            <Form.Label>{translate('SETTINGS_FREQUENCY')}</Form.Label>
                            <Field
                              type="text"
                              name="expirationTimeMonths"
                              maxLength={255}
                              value={values.expirationTimeMonths || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="2d763afc-a310-494c-925c-4561e281e93a-frequency"
                            />
                            <ErrorMessageStyled name="expirationTimeMonths" />
                          </StyledFormGroup>
                        </DivLeftIntended>
                      )}

                      {/* Document Autonaming Checkbox */}
                      <StyledFormGroup controlId="ef1b9a26-c7dd-4b21-b4b9-a4d5e977caa1" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="ef1b9a26-c7dd-4b21-b4b9-a4d5e977caa1">
                            <FormikField
                              type="checkbox"
                              name="autonamingEnabled"
                              checked={!!values.autonamingEnabled}
                              id="ef1b9a26-c7dd-4b21-b4b9-a4d5e977caa1"
                            />
                            <b>{translate('DOC_AUT')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Autonaming Fields - shown when Document Autonaming is checked */}
                      {values.autonamingEnabled && (
                        <DivLeftIntended>
                          {/* Sequence Number Field */}
                          <StyledFormGroup controlId="ab084241-d542-4d30-bfed-0226cdeb795f">
                            <Form.Label>{translate('SETTINGS_SEQUENCE_NUMBER')}</Form.Label>
                            <Field
                              type="number"
                              name="autonamingSeqNo"
                              value={values.autonamingSeqNo || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="ab084241-d542-4d30-bfed-0226cdeb795f"
                            />
                            <ErrorMessageStyled name="autonamingSeqNo" />
                          </StyledFormGroup>

                          {/* Filename Field */}
                          <StyledFormGroup controlId="e2783c91-bdc8-4165-85c6-0d6944b1ff99">
                            <Form.Label>{translate('FILENAME')}</Form.Label>
                            <Field
                              type="text"
                              name="autonamingTemplate"
                              maxLength={255}
                              value={values.autonamingTemplate || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="e2783c91-bdc8-4165-85c6-0d6944b1ff99"
                            />
                            <ErrorMessageStyled name="autonamingTemplate" />
                          </StyledFormGroup>
                        </DivLeftIntended>
                      )}

                      {/* Preserve Draft Versions Checkbox */}
                      <StyledFormGroup controlId="e2202507-39bf-4a27-b563-1dfdd8d3e8be" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="e2202507-39bf-4a27-b563-1dfdd8d3e8be">
                            <FormikField
                              type="checkbox"
                              name="keepDraftVersionsAfterApproval"
                              checked={!!values.keepDraftVersionsAfterApproval}
                              id="e2202507-39bf-4a27-b563-1dfdd8d3e8be"
                            />
                            <b>{translate('SETTINGS_DOC_TYPES_KEEP_DRAFT_VERSION')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Allow Document Amendments Checkbox */}
                      <StyledFormGroup controlId="5ad40c7c-b978-49cb-8d5a-ef9dfc414ca8" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="5ad40c7c-b978-49cb-8d5a-ef9dfc414ca8">
                            <FormikField
                              type="checkbox"
                              name="amendmentsEnabled"
                              checked={!!values.amendmentsEnabled}
                              id="5ad40c7c-b978-49cb-8d5a-ef9dfc414ca8"
                            />
                            <b>{translate('SETTINGS_EDIT_ALLOW')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Prompt Unblinded Users to Set Document Blinding Status */}
                      <StyledFormGroup controlId="1a66742c-1e39-41f3-a64c-3abffbc21221" className="w100p">
                        <Form.Label>{translate('SETTINGS_PROMPT_UNBLINDED_USER_FOR_BLINDING_STATUS')}</Form.Label>
                        <DivLeftIntended>
                          <div className="radio">
                            <label htmlFor="ks1994e5c8-73ef-4bf3-b7b3-254b6bfe805f-true">
                              <FormikField
                                type="radio"
                                name="allowBlindingStatusChange"
                                value="true"
                                checked={values.allowBlindingStatusChange === true}
                                onChange={() => setFieldValue('allowBlindingStatusChange', true)}
                                id="ks1994e5c8-73ef-4bf3-b7b3-254b6bfe805f-true"
                                inline
                              />
                              <strong>{translate('YES')}</strong>
                            </label>
                          </div>
                          <div className="radio">
                            <label htmlFor="ks1994e5c8-73ef-4bf3-b7b3-254b6bfe805f-false">
                              <FormikField
                                type="radio"
                                name="allowBlindingStatusChange"
                                value="false"
                                checked={values.allowBlindingStatusChange === false}
                                onChange={() => setFieldValue('allowBlindingStatusChange', false)}
                                id="ks1994e5c8-73ef-4bf3-b7b3-254b6bfe805f-false"
                                inline
                              />
                              <strong>{translate('NO')}</strong>
                            </label>
                          </div>
                        </DivLeftIntended>
                      </StyledFormGroup>

                      {/* Restrict Users from Reviewing/Approving Documents they Upload or Classify */}
                      <StyledFormGroup controlId="3d7e2ea7-2a82-4998-8b7b-109eb964f4f3" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="3d7e2ea7-2a82-4998-8b7b-109eb964f4f3">
                            <FormikField
                              type="checkbox"
                              name="isInitiatorReviewerOverlapCheckEnabled"
                              checked={!!values.isInitiatorReviewerOverlapCheckEnabled}
                              id="3d7e2ea7-2a82-4998-8b7b-109eb964f4f3"
                            />
                            <b>{translate('RESTRICT_USERS_FROM_REVIEWING_AND_APPROVING_DOCS')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Configure Due Date Checkbox */}
                      <StyledFormGroup controlId="configure-due-date-checkbox" className="w100p">
                        <div className="checkbox">
                          <label htmlFor="configure-due-date-checkbox">
                            <FormikField
                              type="checkbox"
                              name="dueDateEnabled"
                              checked={!!values.dueDateEnabled}
                              id="configure-due-date-checkbox"
                            />
                            <b>{translate('CONFIGURE_DUE_DATE')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>

                      {/* Configure Due Date Fields - shown when Configure Due Date is checked */}
                      {values.dueDateEnabled && (
                        <DivLeftIntended>
                          {/* Due Date Offset Field */}
                          <StyledFormGroup controlId="due-date-offset">
                            <Form.Label>
                              {translate('CONFIGURE_DUE_DATE_TOOLTIP_DUE')}
                              <InfoIconWrapper className="glyphicon glyphicon-info-sign">
                                <div className="info-box-tooltip">
                                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                    {translate('DUE_DATE_INFO_SUBJECT')}
                                  </div>
                                  <div>{translate('DUE_DATE_INFO_BODY')}</div>
                                </div>
                              </InfoIconWrapper>
                            </Form.Label>
                            <Field
                              type="number"
                              name="dueDateOffset"
                              min={0}
                              max={255}
                              value={values.dueDateOffset || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="due-date-offset"
                            />
                            <ErrorMessageStyled name="dueDateOffset" />
                          </StyledFormGroup>
                        </DivLeftIntended>
                      )}
                    </HalfWidthDiv>
                  </form>
                </Modal.Body>
              </Loader>
              <Modal.Footer>
                <button
                  type="button"
                  className="btn btn-chita btn-link text-capitalize"
                  onClick={closeDialog}
                  data-testid="cancel-button"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-capitalize"
                  onClick={() => handleSubmit()}
                  disabled={!!loading || !!isLoadingData}
                  data-testid="save-button"
                >
                  {translate('SAVE')}
                </button>
              </Modal.Footer>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

export default EditForm
